import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import MarketingDashboard from '../components/marketing/MarketingDashboard';
import { format, startOfWeek, endOfWeek, addDays, parseISO } from 'date-fns';
import {
  Users, Eye, Heart, IndianRupee, List, PenLine, TrendingUp,
  ChevronDown, Link2, Calendar, Zap, Megaphone, Plus, X, Lock, AlertTriangle
} from 'lucide-react';

const LONGTERM_CATEGORIES = [
  { key: 'retainership', label: 'Retainership / PR Agency' },
  { key: 'meta_ads', label: 'Meta Ads' },
  { key: 'google_ads', label: 'Google Ads' },
  { key: 'swiggy_cpc', label: 'Swiggy CPC' },
  { key: 'swiggy_ads', label: 'Swiggy Ads' },
  { key: 'zomato_cpc', label: 'Zomato CPC' },
  { key: 'zomato_ads', label: 'Zomato Ads' },
  { key: 'hoardings', label: 'Hoardings' },
];

const EMPTY_LT = () => LONGTERM_CATEGORIES.map(c => ({ category: c.key, cost: '', start_date: '', end_date: '', platform: '' }));

const EMPTY_ADHOC = {
  influencers: { cost: '', post_link: '', date: '', platform: '' },
  paper_ads: { cost: '', link: '', date: '', platform: '' },
  lsm: { cost: '', date: '', platform: '' },
  sponsorships: { cost: '', date: '', platform: '' },
  others: { cost: '', date: '', platform: '' },
};

const EMPTY_CUSTOM = { label: '', amount: '', platform: '' };

// ITER-marketing-pass1 — canonical option list for the compact platform
// selector attached to expense rows. Category (what the spend is for)
// remains separate from Platform (where the activity happened).
const PLATFORM_OPTIONS = [
  { value: '', label: '—' },
  { value: 'Instagram', label: 'Instagram / Meta' },
  { value: 'Google', label: 'Google' },
  { value: 'Swiggy', label: 'Swiggy' },
  { value: 'Zomato', label: 'Zomato' },
  { value: 'Influencer', label: 'Influencer' },
  { value: 'Print', label: 'Print' },
  { value: 'LSM', label: 'Offline / LSM' },
  { value: 'Other', label: 'Other' },
];

function SectionHeader({ icon: Icon, title, open, onToggle, badge }) {
  return (
    <CollapsibleTrigger asChild>
      <button type="button" onClick={onToggle}
        className="flex items-center justify-between w-full py-2.5 px-3 bg-[#F9F9F8] rounded-lg hover:bg-[#F3F5F2] transition-colors text-left"
        data-testid={`section-${title.toLowerCase().replace(/\s/g, '-')}`}>
        <span className="flex items-center gap-2 text-sm font-medium text-[#1A2421]">
          <Icon className="w-4 h-4 text-[color:var(--color-primary)]" /> {title}
          {badge && <Badge variant="outline" className="text-[10px] border-[#E2E8DF] text-[#5C6B64] ml-1">{badge}</Badge>}
        </span>
        <ChevronDown className={`w-4 h-4 text-[#5C6B64] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
    </CollapsibleTrigger>
  );
}

// ITER286-refine — read-only auto-calculated KPI cell used in the
// Auto-Calculated Summary section under the Marketing Entry form.
function SummaryCell({ label, value, color = 'text-[#1A2421]', hint, testid }) {
  return (
    <div className="rounded border border-[#E2E8DF] bg-[#F9F9F8] px-2 py-2" data-testid={testid}>
      <p className="text-[9px] uppercase tracking-wider text-[#5C6B64] font-medium leading-tight">
        {label}
      </p>
      <p className={`text-sm font-bold mt-1 ${color}`}>{value}</p>
      {hint && <p className="text-[9px] text-[#5C6B64]/70 mt-1 leading-tight">{hint}</p>}
    </div>
  );
}

export default function ManagerMarketing() {
  const { getHeaders, API, canEditModule } = useAuth();
  const moduleWritable = canEditModule('marketing');
  const [tab, setTab] = useState('dashboard');
  const now = new Date();
  const ws = format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const we = format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const todayStr = format(now, 'yyyy-MM-dd');

  const [form, setForm] = useState({
    week_start: ws, week_end: we,
    opening_followers: '', closing_followers: '',
    num_posts: '', total_views: '', highest_views: '', lowest_views: '',
    total_engagement: '', highest_engagement: '',
    best_post_link: '', paid_ads_link: '',
    // ITER-marketing-pass1 — restore the previously-dropped signals
    reach: '', impressions: '',
    likes: '', comments: '', shares: '', saves: '',
    num_reels: '', num_stories: '', num_videos: '',
    attributed_revenue: '',
  });
  const [longterm, setLongterm] = useState(EMPTY_LT());
  const [adhoc, setAdhoc] = useState({ ...EMPTY_ADHOC });
  const [customCosts, setCustomCosts] = useState([{ ...EMPTY_CUSTOM }]);
  const [submitting, setSubmitting] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [autoFilled, setAutoFilled] = useState(false);
  const [autoFillSource, setAutoFillSource] = useState(null);
  const [weekLocked] = useState(true); // always locked — system-defined

  // Week info from backend (current + previous)
  const [weekInfo, setWeekInfo] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState('current'); // 'current' or 'previous'

  // Budget state (read-only display)
  const [budget, setBudget] = useState({ amount: '', edit_count: 0, locked: false });
  const budgetMonth = now.getMonth() + 1;
  const budgetYear = now.getFullYear();

  // Collapsible sections
  const [openSections, setOpenSections] = useState({ content: true, budget: true, longterm: true, adhoc: true, custom: false });
  const toggle = (s) => setOpenSections(p => ({ ...p, [s]: !p[s] }));

  // Load week info from system date (not previous entry)
  useEffect(() => {
    axios.get(`${API}/manager/marketing/week-info`, { headers: getHeaders() })
      .then(res => {
        setWeekInfo(res.data);
        // Default to current week
        const cw = res.data.current_week;
        setForm(p => ({ ...p, week_start: cw.start, week_end: cw.end }));
      }).catch(() => {});
  }, [API, getHeaders]);

  // Pick ANY past Monday via date-picker; snaps to Mon-Sun.
  const pickCustomWeek = useCallback((d) => {
    if (!d) return;
    const mon = startOfWeek(d, { weekStartsOn: 1 });
    const sun = endOfWeek(d, { weekStartsOn: 1 });
    const ws2 = format(mon, 'yyyy-MM-dd');
    const we2 = format(sun, 'yyyy-MM-dd');
    setSelectedWeek('custom');
    setForm(p => ({ ...p, week_start: ws2, week_end: we2 }));
    loadExistingEntry(ws2, we2);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load opening followers from previous closing
  useEffect(() => {
    axios.get(`${API}/manager/marketing/previous-closing`, { headers: getHeaders() })
      .then(res => {
        const { closing_followers, from_week } = res.data;
        if (closing_followers > 0) {
          setForm(p => ({ ...p, opening_followers: closing_followers }));
          setAutoFilled(true);
          setAutoFillSource(from_week);
        }
      }).catch(() => {});
  }, [API, getHeaders]);

  // When week changes, load existing entry
  const switchWeek = (which) => {
    if (!weekInfo) return;
    setSelectedWeek(which);
    const w = which === 'current' ? weekInfo.current_week : weekInfo.previous_week;
    setForm(p => ({ ...p, week_start: w.start, week_end: w.end }));
    // Load existing entry for this week
    loadExistingEntry(w.start, w.end);
  };

  const loadExistingEntry = useCallback((ws, we) => {
    axios.get(`${API}/manager/marketing`, { headers: getHeaders() })
      .then(res => {
        const existing = res.data.find(e => e.week_start === ws && e.week_end === we);
        if (existing) {
          setForm(p => ({
            ...p, week_start: ws, week_end: we,
            opening_followers: existing.opening_followers || '',
            closing_followers: existing.closing_followers || '',
            num_posts: existing.num_posts || '', total_views: existing.total_views || '',
            highest_views: existing.highest_views || '', lowest_views: existing.lowest_views || '',
            total_engagement: existing.total_engagement || '', highest_engagement: existing.highest_engagement || '',
            best_post_link: existing.best_post_link || '', paid_ads_link: existing.paid_ads_link || '',
            // ITER-marketing-pass1 — restore signals so edits don't overwrite with 0
            reach: existing.reach || '', impressions: existing.impressions || '',
            likes: existing.likes || '', comments: existing.comments || '',
            shares: existing.shares || '', saves: existing.saves || '',
            num_reels: existing.num_reels || '', num_stories: existing.num_stories || '',
            num_videos: existing.num_videos || '',
            attributed_revenue: existing.attributed_revenue || '',
          }));
          if (existing.longterm_expenses?.length) {
            const restored = LONGTERM_CATEGORIES.map(c => {
              const found = existing.longterm_expenses.find(lt => lt.category === c.key);
              return found ? { category: c.key, cost: found.cost || '', start_date: found.start_date || '', end_date: found.end_date || '', platform: found.platform || '' }
                : { category: c.key, cost: '', start_date: '', end_date: '', platform: '' };
            });
            setLongterm(restored);
          }
          if (existing.adhoc_expenses) {
            const ae = existing.adhoc_expenses;
            setAdhoc({
              influencers: { cost: ae.influencers?.cost || '', post_link: ae.influencers?.post_link || '', date: ae.influencers?.date || '', platform: ae.influencers?.platform || '' },
              paper_ads: { cost: ae.paper_ads?.cost || '', link: ae.paper_ads?.link || '', date: ae.paper_ads?.date || '', platform: ae.paper_ads?.platform || '' },
              lsm: { cost: ae.lsm?.cost || '', date: ae.lsm?.date || '', platform: ae.lsm?.platform || '' },
              sponsorships: { cost: ae.sponsorships?.cost || '', date: ae.sponsorships?.date || '', platform: ae.sponsorships?.platform || '' },
              others: { cost: ae.others?.cost || '', date: ae.others?.date || '', platform: ae.others?.platform || '' },
            });
          }
          if (existing.custom_costs?.length) {
            setCustomCosts(existing.custom_costs.map(c => ({ label: c.label, amount: c.amount, platform: c.platform || '' })));
          }
        }
      }).catch(() => {});
  }, [API, getHeaders]);

  // Load budget
  useEffect(() => {
    axios.get(`${API}/manager/marketing/budget?year=${budgetYear}&month=${budgetMonth}`, { headers: getHeaders() })
      .then(res => setBudget({ amount: res.data.budget || '', edit_count: res.data.edit_count, locked: res.data.locked }))
      .catch(() => {});
  }, [API, getHeaders, budgetYear, budgetMonth]);

  const loadEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const res = await axios.get(`${API}/manager/marketing`, { headers: getHeaders() });
      setEntries(res.data);
    } catch {}
    finally { setLoadingEntries(false); }
  }, [API, getHeaders]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  // Computed totals
  const longtermTotal = useMemo(() => {
    return longterm.reduce((s, lt) => {
      if (!lt.cost || parseFloat(lt.cost) <= 0) return s;
      const cost = parseFloat(lt.cost);
      if (!lt.start_date || !lt.end_date) return s + cost;
      // Simple prorate: overlap of week with duration
      const ws = new Date(form.week_start); const we = new Date(form.week_end);
      const ls = new Date(lt.start_date); const le = new Date(lt.end_date);
      const totalDays = Math.max(1, (le - ls) / 86400000 + 1);
      const overlapStart = new Date(Math.max(ws, ls)); const overlapEnd = new Date(Math.min(we, le));
      const overlapDays = Math.max(0, (overlapEnd - overlapStart) / 86400000 + 1);
      return s + Math.round(cost * overlapDays / totalDays);
    }, 0);
  }, [longterm, form.week_start, form.week_end]);

  // Adhoc: classify each item as actual/planned based on date vs today
  const classifyAdhoc = (dateStr) => (!dateStr || dateStr <= todayStr) ? 'actual' : 'planned';

  const adhocActualTotal = useMemo(() =>
    Object.entries(adhoc).reduce((s, [, v]) => s + (classifyAdhoc(v.date) === 'actual' ? (parseFloat(v.cost) || 0) : 0), 0)
  , [adhoc, todayStr]);

  const adhocPlannedTotal = useMemo(() =>
    Object.entries(adhoc).reduce((s, [, v]) => s + (classifyAdhoc(v.date) === 'planned' ? (parseFloat(v.cost) || 0) : 0), 0)
  , [adhoc, todayStr]);

  const adhocTotal = adhocActualTotal; // Only actual counts in totals

  const customTotal = useMemo(() =>
    customCosts.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0)
  , [customCosts]);

  const totalSpend = longtermTotal + adhocTotal + customTotal;

  // ITER-marketing-pass1 — MTD spend/budget now come from the canonical
  // executive rollup (week_end month bucketing) so Manager, Admin MTD and
  // Executive Dashboard show the SAME number for the SAME outlet/month.
  // No more independent client-side `week_start.startsWith(YYYY-MM)` sum.
  const [mtdCanonical, setMtdCanonical] = useState({ spend: 0, budget: 0 });
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/manager/marketing/dashboard?period=mtd&year=${budgetYear}&month=${budgetMonth}`,
      { headers: getHeaders() })
      .then(res => {
        if (cancelled) return;
        const cur = res.data?.current || {};
        setMtdCanonical({
          spend: Number(cur.total_spend || 0),
          budget: Number(cur.budget || 0),
        });
      }).catch(() => { if (!cancelled) setMtdCanonical({ spend: 0, budget: 0 }); });
    return () => { cancelled = true; };
  }, [API, getHeaders, budgetYear, budgetMonth, entries]);

  const mtdSpend = mtdCanonical.spend;

  const budgetAmt = parseFloat(budget.amount) || 0;
  const budgetRemaining = budgetAmt - mtdSpend;
  const budgetUtilPct = budgetAmt > 0 ? Math.round(mtdSpend / budgetAmt * 100) : 0;
  const budgetColor = budgetUtilPct >= 100 ? 'text-[#C45A41]' : budgetUtilPct >= 75 ? 'text-[#D4A017]' : 'text-[#2D6A4F]';
  const budgetBarColor = budgetUtilPct >= 100 ? 'bg-[#C45A41]' : budgetUtilPct >= 75 ? 'bg-[#D4A017]' : 'bg-[#2D6A4F]';

  const val = (key) => form[key];
  const set = (key, v) => setForm(p => ({ ...p, [key]: v }));

  // ITER286-refine — auto-compute total_engagement from breakdown
  // when likes+comments+shares+saves are all populated. Manager can
  // still type total_engagement directly if they only have the lump
  // sum; the auto-fill only fires when the four channel counts are
  // non-empty (any of them). Runs on any change to the four inputs.
  useEffect(() => {
    const parts = ['likes', 'comments', 'shares', 'saves']
      .map(k => parseInt(form[k], 10)).filter(n => !isNaN(n));
    if (parts.length === 0) return;
    const sum = parts.reduce((a, b) => a + b, 0);
    if (sum !== parseInt(form.total_engagement, 10)) {
      setForm(p => ({ ...p, total_engagement: sum }));
    }
  }, [form.likes, form.comments, form.shares, form.saves, form.total_engagement]);

  // Validation snapshot — read-only derivations for warning banners.
  const openF   = parseInt(form.opening_followers, 10) || 0;
  const closeF  = parseInt(form.closing_followers, 10) || 0;
  const growth  = closeF - openF;
  const growthPct = openF > 0 ? (growth / openF) * 100 : null;
  const hasNegativeGrowth = openF > 0 && closeF > 0 && growth < 0;
  const openingHigherThanClosing = hasNegativeGrowth;
  const numPostsN = parseInt(form.num_posts, 10) || 0;
  const totalViewsN = parseInt(form.total_views, 10) || 0;
  const reachN      = parseInt(form.reach, 10) || 0;
  const impressionsN = parseInt(form.impressions, 10) || 0;
  const engagementN  = parseInt(form.total_engagement, 10) || 0;
  const avgViews   = numPostsN > 0 ? Math.round(totalViewsN / numPostsN) : null;
  const avgReach   = numPostsN > 0 ? Math.round(reachN / numPostsN) : null;
  const avgImpr    = numPostsN > 0 ? Math.round(impressionsN / numPostsN) : null;
  const engagementRatePct = closeF > 0 ? (engagementN / closeF * 100) : null;

  const setLT = (idx, prop, v) => {
    const u = [...longterm]; u[idx] = { ...u[idx], [prop]: v }; setLongterm(u);
  };
  const setAd = (cat, prop, v) => {
    setAdhoc(p => ({ ...p, [cat]: { ...p[cat], [prop]: v } }));
  };

  // ITER-marketing-pass1 — compact platform selector. Category (what the
  // spend was for) and Platform (where the activity happened) are kept
  // as separate axes. Empty '' means "not tagged".
  const PlatformSelect = ({ value, onChange, testId }) => (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      data-testid={testId}
      className="h-8 text-[11px] rounded-md border border-[#E2E8DF] bg-white px-2 text-[#1A2421] focus:outline-none focus:ring-1 focus:ring-[color:var(--color-primary)]"
    >
      {PLATFORM_OPTIONS.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.week_start || !form.week_end) { toast.error('Set week dates'); return; }
    if (!form.closing_followers) { toast.error('Enter closing followers'); return; }
    // Validate: influencer cost requires post_link
    if ((parseFloat(adhoc.influencers.cost) || 0) > 0 && !adhoc.influencers.post_link) {
      toast.error('Post link required for Influencer cost'); return;
    }
    // Validate: longterm cost requires dates
    for (const lt of longterm) {
      if ((parseFloat(lt.cost) || 0) > 0 && (!lt.start_date || !lt.end_date)) {
        const cat = LONGTERM_CATEGORIES.find(c => c.key === lt.category);
        toast.error(`Start & end dates required for ${cat?.label || lt.category}`); return;
      }
    }
    // Validate URLs
    const urlFields = [
      { val: form.best_post_link, name: 'Best post link' },
      { val: form.paid_ads_link, name: 'Paid ads link' },
      { val: adhoc.influencers.post_link, name: 'Influencer post link' },
      { val: adhoc.paper_ads.link, name: 'Paper ads link' },
    ];
    for (const uf of urlFields) {
      if (uf.val && uf.val.trim() && !uf.val.match(/^https?:\/\/.+/)) {
        toast.error(`${uf.name} must be a valid URL (http/https)`); return;
      }
    }
    // Validate custom
    const labels = customCosts.filter(c => c.label && (parseFloat(c.amount) || 0) > 0).map(c => c.label);
    if (labels.length !== new Set(labels).size) { toast.error('Duplicate custom labels'); return; }
    for (const c of customCosts) {
      if ((parseFloat(c.amount) || 0) > 0 && !c.label) { toast.error('Label required for custom cost'); return; }
    }

    setSubmitting(true);
    try {
      const payload = {
        week_start: form.week_start, week_end: form.week_end,
        opening_followers: parseInt(form.opening_followers) || 0,
        closing_followers: parseInt(form.closing_followers) || 0,
        num_posts: parseInt(form.num_posts) || 0,
        total_views: parseInt(form.total_views) || 0,
        highest_views: parseInt(form.highest_views) || 0,
        lowest_views: parseInt(form.lowest_views) || 0,
        total_engagement: parseInt(form.total_engagement) || 0,
        highest_engagement: parseInt(form.highest_engagement) || 0,
        best_post_link: form.best_post_link,
        paid_ads_link: form.paid_ads_link,
        // ITER-marketing-pass1 — send ALL entered signals so the
        // dashboard rollup can represent everything captured in the form.
        reach:              parseInt(form.reach) || 0,
        impressions:        parseInt(form.impressions) || 0,
        likes:              parseInt(form.likes) || 0,
        comments:           parseInt(form.comments) || 0,
        shares:             parseInt(form.shares) || 0,
        saves:              parseInt(form.saves) || 0,
        num_reels:          parseInt(form.num_reels) || 0,
        num_stories:        parseInt(form.num_stories) || 0,
        num_videos:         parseInt(form.num_videos) || 0,
        attributed_revenue: parseFloat(form.attributed_revenue) || 0,
        longterm_expenses: longterm.filter(lt => (parseFloat(lt.cost) || 0) > 0).map(lt => ({
          category: lt.category, cost: parseFloat(lt.cost),
          start_date: lt.start_date, end_date: lt.end_date,
          platform: lt.platform || '',
        })),
        adhoc_expenses: {
          // ITER-marketing-pass1 — dates + platform now round-trip so the
          // backend can classify Planned vs Actual under classify_adhoc_type().
          influencers:  { cost: parseFloat(adhoc.influencers.cost)  || 0, post_link: adhoc.influencers.post_link || '', date: adhoc.influencers.date  || '', platform: adhoc.influencers.platform  || '' },
          paper_ads:    { cost: parseFloat(adhoc.paper_ads.cost)    || 0, link:      adhoc.paper_ads.link       || '', date: adhoc.paper_ads.date    || '', platform: adhoc.paper_ads.platform    || '' },
          lsm:          { cost: parseFloat(adhoc.lsm.cost)          || 0, date: adhoc.lsm.date          || '', platform: adhoc.lsm.platform          || '' },
          sponsorships: { cost: parseFloat(adhoc.sponsorships.cost) || 0, date: adhoc.sponsorships.date || '', platform: adhoc.sponsorships.platform || '' },
          others:       { cost: parseFloat(adhoc.others.cost)       || 0, date: adhoc.others.date       || '', platform: adhoc.others.platform       || '' },
        },
        custom_costs: customCosts.filter(c => c.label && (parseFloat(c.amount) || 0) > 0).map(c => ({
          label: c.label, amount: parseFloat(c.amount), platform: c.platform || '',
        })),
      };
      await axios.post(`${API}/manager/marketing`, payload, { headers: getHeaders() });
      toast.success(`Saved — Total Spend: ₹${Math.round(totalSpend).toLocaleString('en-IN')}`);
      loadEntries();
      setTab('list');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const loadEntry = (entry) => {
    setForm({
      week_start: entry.week_start, week_end: entry.week_end,
      opening_followers: entry.opening_followers || '', closing_followers: entry.closing_followers || '',
      num_posts: entry.num_posts || '', total_views: entry.total_views || '',
      highest_views: entry.highest_views || '', lowest_views: entry.lowest_views || '',
      total_engagement: entry.total_engagement || '', highest_engagement: entry.highest_engagement || '',
      best_post_link: entry.best_post_link || '', paid_ads_link: entry.paid_ads_link || '',
      // ITER-marketing-pass1 — restore signals from history edit path too
      reach: entry.reach || '', impressions: entry.impressions || '',
      likes: entry.likes || '', comments: entry.comments || '',
      shares: entry.shares || '', saves: entry.saves || '',
      num_reels: entry.num_reels || '', num_stories: entry.num_stories || '',
      num_videos: entry.num_videos || '',
      attributed_revenue: entry.attributed_revenue || '',
    });
    // Load longterm — preserve platform on edit
    const lt = EMPTY_LT();
    (entry.longterm_expenses || []).forEach(ex => {
      const idx = lt.findIndex(l => l.category === ex.category);
      if (idx >= 0) {
        lt[idx] = { ...lt[idx], cost: ex.cost, start_date: ex.start_date, end_date: ex.end_date, platform: ex.platform || '' };
      }
    });
    setLongterm(lt);
    // Load adhoc — preserve date AND platform on edit
    const ad = entry.adhoc_expenses || {};
    setAdhoc({
      influencers:  { cost: ad.influencers?.cost  || '', post_link: ad.influencers?.post_link || '', date: ad.influencers?.date  || '', platform: ad.influencers?.platform  || '' },
      paper_ads:    { cost: ad.paper_ads?.cost    || '', link:      ad.paper_ads?.link       || '', date: ad.paper_ads?.date    || '', platform: ad.paper_ads?.platform    || '' },
      lsm:          { cost: ad.lsm?.cost          || '', date: ad.lsm?.date          || '', platform: ad.lsm?.platform          || '' },
      sponsorships: { cost: ad.sponsorships?.cost || '', date: ad.sponsorships?.date || '', platform: ad.sponsorships?.platform || '' },
      others:       { cost: ad.others?.cost       || '', date: ad.others?.date       || '', platform: ad.others?.platform       || '' },
    });
    // Load custom — preserve platform on edit
    const cc = (entry.custom_costs || []).map(c => ({ label: c.label, amount: c.amount, platform: c.platform || '' }));
    if (cc.length === 0) cc.push({ ...EMPTY_CUSTOM });
    setCustomCosts(cc);
    setTab('form');
  };

  const hasLtSpend = longtermTotal > 0;
  const hasAdSpend = adhocTotal > 0;

  return (
    <div data-testid="manager-marketing-page">
      <div className="mb-5">
        <h2 className="text-xl sm:text-2xl tracking-tight font-semibold text-[#1A2421]">Marketing</h2>
        <p className="text-sm text-[#5C6B64] mt-1">Marketing performance &amp; campaign analytics</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F3F5F2] p-0.5 rounded-lg w-fit mb-5">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4 gap-1.5"
            data-testid="mkt-tab-dashboard">📊 Dashboard</TabsTrigger>
          <TabsTrigger value="form" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4 gap-1.5"
            data-testid="mkt-tab-form"><PenLine className="w-3.5 h-3.5" /> Entry</TabsTrigger>
          <TabsTrigger value="list" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4 gap-1.5"
            data-testid="mkt-tab-list"><List className="w-3.5 h-3.5" /> History</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <MarketingDashboard mode="manager" />
        </TabsContent>

        <TabsContent value="form">
          <form onSubmit={handleSubmit} className="space-y-4 max-w-none xl:max-w-[1400px] 2xl:max-w-[1600px] mx-auto">
            {/* Week period — system-defined, current or previous */}
            <Card className="border-[#E2E8DF] shadow-sm">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-[#5C6B64] flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Reporting Week
                  </p>
                  <Lock className="w-3.5 h-3.5 text-[#2D6A4F]" />
                </div>
                {weekInfo && (
                  <div className="flex gap-2 mb-2" data-testid="week-selector">
                    <button type="button" onClick={() => switchWeek('current')}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${selectedWeek === 'current' ? 'bg-[color:var(--color-primary)] text-white' : 'bg-[#F3F5F2] text-[#5C6B64] hover:bg-[#E2E8DF]'}`}
                      data-testid="week-current-btn">
                      Current Week
                      {weekInfo.current_week.has_entry && <span className="ml-1 text-[9px]">(Saved)</span>}
                    </button>
                    <button type="button" onClick={() => switchWeek('previous')}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${selectedWeek === 'previous' ? 'bg-[color:var(--color-primary)] text-white' : 'bg-[#F3F5F2] text-[#5C6B64] hover:bg-[#E2E8DF]'}`}
                      data-testid="week-previous-btn">
                      Previous Week
                      {weekInfo.previous_week.has_entry && <span className="ml-1 text-[9px]">(Saved)</span>}
                    </button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button"
                          className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${selectedWeek === 'custom' ? 'bg-[color:var(--color-primary)] text-white' : 'bg-[#F3F5F2] text-[#5C6B64] hover:bg-[#E2E8DF]'}`}
                          data-testid="week-custom-btn">
                          Pick Week
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <CalendarPicker mode="single"
                          selected={form.week_start ? parseISO(form.week_start) : undefined}
                          onSelect={pickCustomWeek}
                          disabled={(d) => d > new Date()}
                          weekStartsOn={1}
                          showOutsideDays
                          initialFocus />
                        <div className="px-3 py-2 border-t border-[#E2E8DF] text-[10px] text-[#5C6B64]">
                          Tap any day — the week snaps to Mon–Sun automatically.
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
                <p className="text-sm font-medium text-[#1A2421]" data-testid="mkt-week-display">
                  Week: {form.week_start ? format(parseISO(form.week_start), 'd MMM') : '—'} – {form.week_end ? format(parseISO(form.week_end), 'd MMM, yyyy') : '—'}
                </p>
                <p className="text-[10px] text-[#2D6A4F] mt-1" data-testid="week-auto-set">
                  Mon–Sun cycle · enter current, previous, or any past week you need to backfill
                </p>
              </CardContent>
            </Card>

            {/* ITER286-refine — main 2-column grid so desktop uses the full width */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid="mkt-form-grid">
            <div className="lg:col-span-7 space-y-3">

            {/* Social Media + Content */}
            <Collapsible open={openSections.content} onOpenChange={() => toggle('content')}>
              <SectionHeader icon={Eye} title="Content & Social" open={openSections.content} onToggle={() => {}} />
              <CollapsibleContent className="mt-2 space-y-3">
                <Card className="border-[#E2E8DF] shadow-sm">
                  <CardContent className="pt-3 pb-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3 items-end">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-[#5C6B64]">
                          Opening Followers {autoFilled && <span className="text-[10px] text-[#2D6A4F]">(auto)</span>}
                        </Label>
                        <Input type="number" inputMode="numeric" value={val('opening_followers')}
                          onChange={e => { set('opening_followers', e.target.value); setAutoFilled(false); setAutoFillSource(null); }}
                          placeholder="0" className="h-10 border-[#E2E8DF]" data-testid="mkt-opening-followers" />
                        {autoFilled && autoFillSource && (
                          <p className="text-[10px] text-[#2D6A4F]" data-testid="followers-auto-source">
                            Auto-filled from {autoFillSource}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-[#5C6B64]">Closing Followers</Label>
                        <Input type="number" inputMode="numeric" value={val('closing_followers')}
                          onChange={e => set('closing_followers', e.target.value)}
                          placeholder="0" className="h-10 border-[#E2E8DF]" data-testid="mkt-closing-followers" />
                      </div>
                    </div>
                    {parseInt(form.closing_followers) > 0 && parseInt(form.opening_followers) > 0 && (() => {
                      const g = parseInt(form.closing_followers) - parseInt(form.opening_followers);
                      return (
                        <Badge className={`text-[10px] ${g >= 0 ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#C45A41]/10 text-[#C45A41]'}`}
                          data-testid="mkt-growth-badge">
                          <TrendingUp className="w-2.5 h-2.5 mr-1" />{g >= 0 ? '+' : ''}{g} ({(g / parseInt(form.opening_followers) * 100).toFixed(1)}%)
                        </Badge>
                      );
                    })()}
                    {openingHigherThanClosing && (
                      <div className="flex items-center gap-1.5 text-[10px] text-[#C45A41] bg-[#C45A41]/5 border border-[#C45A41]/20 rounded px-2 py-1"
                           data-testid="mkt-warn-negative-growth">
                        <AlertTriangle className="w-3 h-3" />
                        Negative follower growth — verify Opening &amp; Closing Followers.
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[#5C6B64]">Number of Posts</Label>
                      <Input type="number" inputMode="numeric" value={val('num_posts')}
                        onChange={e => set('num_posts', e.target.value)}
                        className="h-10 border-[#E2E8DF]" data-testid="mkt-num-posts" />
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-end">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-[#5C6B64]">Total Views</Label>
                        <Input type="number" inputMode="numeric" value={val('total_views')}
                          onChange={e => set('total_views', e.target.value)}
                          className="h-10 border-[#E2E8DF]" data-testid="mkt-total-views" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-[#5C6B64]">Highest</Label>
                        <Input type="number" inputMode="numeric" value={val('highest_views')}
                          onChange={e => set('highest_views', e.target.value)}
                          className="h-10 border-[#E2E8DF]" data-testid="mkt-highest-views" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-[#5C6B64]">Lowest</Label>
                        <Input type="number" inputMode="numeric" value={val('lowest_views')}
                          onChange={e => set('lowest_views', e.target.value)}
                          className="h-10 border-[#E2E8DF]" data-testid="mkt-lowest-views" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 items-end">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-[#5C6B64]">Total Engagement</Label>
                        <Input type="number" inputMode="numeric" value={val('total_engagement')}
                          onChange={e => set('total_engagement', e.target.value)}
                          className="h-10 border-[#E2E8DF]" data-testid="mkt-total-engagement" />
                        <p className="text-[10px] text-[#5C6B64]/70">Likes + Comments + Shares + Saves</p>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-[#5C6B64]">Highest (single)</Label>
                        <Input type="number" inputMode="numeric" value={val('highest_engagement')}
                          onChange={e => set('highest_engagement', e.target.value)}
                          className="h-10 border-[#E2E8DF]" data-testid="mkt-highest-engagement" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[#5C6B64] flex items-center gap-1"><Link2 className="w-3 h-3" /> Best Performing Post/Reel Link</Label>
                      <Input type="url" value={val('best_post_link')} onChange={e => set('best_post_link', e.target.value)}
                        placeholder="https://..." className="h-10 border-[#E2E8DF] text-sm" data-testid="mkt-best-post-link" />
                    </div>

                    {/* ITER286-refine — additional dashboard signals.
                        All optional; dashboard cells render "—" if blank.
                        Total Engagement is auto-computed when likes/comments
                        /shares/saves are all populated. */}
                    <div className="pt-2 mt-2 border-t border-[#E2E8DF]">
                      <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-medium mb-2">
                        Reach &amp; content mix
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="space-y-1"><Label className="text-xs text-[#5C6B64]">Reach</Label>
                          <Input type="number" min="0" inputMode="numeric" value={val('reach')}
                            onChange={e => set('reach', e.target.value)}
                            className="h-9 border-[#E2E8DF]" data-testid="mkt-reach" /></div>
                        <div className="space-y-1"><Label className="text-xs text-[#5C6B64]">Impressions</Label>
                          <Input type="number" min="0" inputMode="numeric" value={val('impressions')}
                            onChange={e => set('impressions', e.target.value)}
                            className="h-9 border-[#E2E8DF]" data-testid="mkt-impressions" /></div>
                        <div className="space-y-1"><Label className="text-xs text-[#5C6B64]">Reels</Label>
                          <Input type="number" min="0" inputMode="numeric" value={val('num_reels')}
                            onChange={e => set('num_reels', e.target.value)}
                            className="h-9 border-[#E2E8DF]" data-testid="mkt-reels" /></div>
                        <div className="space-y-1"><Label className="text-xs text-[#5C6B64]">Stories</Label>
                          <Input type="number" min="0" inputMode="numeric" value={val('num_stories')}
                            onChange={e => set('num_stories', e.target.value)}
                            className="h-9 border-[#E2E8DF]" data-testid="mkt-stories" /></div>
                        <div className="space-y-1"><Label className="text-xs text-[#5C6B64]">Videos</Label>
                          <Input type="number" min="0" inputMode="numeric" value={val('num_videos')}
                            onChange={e => set('num_videos', e.target.value)}
                            className="h-9 border-[#E2E8DF]" data-testid="mkt-videos" /></div>
                        <div className="space-y-1"><Label className="text-xs text-[#5C6B64]">Attributed Revenue</Label>
                          <Input type="number" min="0" inputMode="decimal" step="any" value={val('attributed_revenue')}
                            onChange={e => set('attributed_revenue', e.target.value)}
                            placeholder="₹0 (optional)"
                            className="h-9 border-[#E2E8DF]" data-testid="mkt-attributed-revenue" /></div>
                      </div>
                      <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-medium mt-3 mb-2">
                        Engagement breakdown (optional — used to auto-compute Total Engagement)
                      </p>
                      <div className="grid grid-cols-4 gap-3">
                        <div className="space-y-1"><Label className="text-xs text-[#5C6B64]">Likes</Label>
                          <Input type="number" min="0" inputMode="numeric" value={val('likes')}
                            onChange={e => set('likes', e.target.value)}
                            className="h-9 border-[#E2E8DF]" data-testid="mkt-likes" /></div>
                        <div className="space-y-1"><Label className="text-xs text-[#5C6B64]">Comments</Label>
                          <Input type="number" min="0" inputMode="numeric" value={val('comments')}
                            onChange={e => set('comments', e.target.value)}
                            className="h-9 border-[#E2E8DF]" data-testid="mkt-comments" /></div>
                        <div className="space-y-1"><Label className="text-xs text-[#5C6B64]">Shares</Label>
                          <Input type="number" min="0" inputMode="numeric" value={val('shares')}
                            onChange={e => set('shares', e.target.value)}
                            className="h-9 border-[#E2E8DF]" data-testid="mkt-shares" /></div>
                        <div className="space-y-1"><Label className="text-xs text-[#5C6B64]">Saves</Label>
                          <Input type="number" min="0" inputMode="numeric" value={val('saves')}
                            onChange={e => set('saves', e.target.value)}
                            className="h-9 border-[#E2E8DF]" data-testid="mkt-saves" /></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>

            </div> {/* end left col */}
            <div className="lg:col-span-5 space-y-3">

            {/* Budget — Read-only (Admin sets budget) */}
            <Collapsible open={openSections.budget} onOpenChange={() => toggle('budget')}>
              <SectionHeader icon={IndianRupee} title="Budget" open={openSections.budget} onToggle={() => {}}
                badge={budgetAmt > 0 ? `₹${budgetAmt.toLocaleString('en-IN')}` : 'Not Set'} />
              <CollapsibleContent className="mt-2">
                <Card className="border-[#E2E8DF] shadow-sm">
                  <CardContent className="pt-3 pb-3">
                    {budgetAmt > 0 ? (
                      <>
                        <div className="grid grid-cols-4 gap-2 text-center" data-testid="mkt-budget-summary">
                          <div>
                            <p className="text-[10px] text-[#5C6B64] uppercase">Budget</p>
                            <p className="text-sm font-bold text-[#1A2421]">₹{budgetAmt.toLocaleString('en-IN')}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[#5C6B64] uppercase">MTD Spent</p>
                            <p className="text-sm font-bold text-[#1A2421]">₹{Math.round(mtdSpend).toLocaleString('en-IN')}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[#5C6B64] uppercase">Remaining</p>
                            <p className={`text-sm font-bold ${budgetRemaining >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>₹{Math.round(budgetRemaining).toLocaleString('en-IN')}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[#5C6B64] uppercase">Used</p>
                            <p className={`text-sm font-bold ${budgetColor}`}>{budgetUtilPct}%</p>
                          </div>
                        </div>
                        <div className="w-full h-2 bg-[#F3F5F2] rounded-full mt-2 overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${budgetBarColor}`}
                            style={{ width: `${Math.min(budgetUtilPct, 100)}%` }} />
                        </div>
                        {budgetUtilPct >= 90 && budgetUtilPct < 100 && (
                          <div className="flex items-center gap-1 mt-1.5">
                            <AlertTriangle className="w-3 h-3 text-[#D4A017]" />
                            <p className="text-[10px] text-[#D4A017]">Approaching budget limit ({budgetUtilPct}%)</p>
                          </div>
                        )}
                        {budgetUtilPct >= 100 && (
                          <div className="flex items-center gap-1 mt-1.5">
                            <AlertTriangle className="w-3 h-3 text-[#C45A41]" />
                            <p className="text-[10px] text-[#C45A41] font-medium">Budget exceeded ({budgetUtilPct}%)</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-[#5C6B64] italic text-center py-2">Budget not set by admin for {format(now, 'MMM yyyy')}</p>
                    )}
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>

            {/* Long-term Expenses */}
            <Collapsible open={openSections.longterm} onOpenChange={() => toggle('longterm')}>
              <SectionHeader icon={Calendar} title="Long-term Expenses" open={openSections.longterm} onToggle={() => {}}
                badge={hasLtSpend ? `₹${longtermTotal.toLocaleString('en-IN')} prorated` : null} />
              <CollapsibleContent className="mt-2">
                <Card className="border-[#E2E8DF] shadow-sm">
                  <CardContent className="pt-3 pb-3 space-y-3">
                    {longterm.map((lt, idx) => {
                      const cat = LONGTERM_CATEGORIES[idx];
                      return (
                        <div key={cat.key} className="space-y-1.5">
                          <Label className="text-xs text-[#5C6B64]">{cat.label}</Label>
                          <div className="grid grid-cols-3 gap-2 items-end">
                            <div>
                              <Input type="number" inputMode="decimal" step="any" placeholder="₹0" value={lt.cost}
                                onChange={e => setLT(idx, 'cost', e.target.value)}
                                className="h-10 border-[#E2E8DF] text-sm" data-testid={`lt-cost-${cat.key}`} />
                            </div>
                            <div>
                              <Input type="date" value={lt.start_date} onChange={e => setLT(idx, 'start_date', e.target.value)}
                                min={form.week_start}
                                className="h-10 border-[#E2E8DF] text-xs" data-testid={`lt-start-${cat.key}`} />
                            </div>
                            <div>
                              <Input type="date" value={lt.end_date} onChange={e => setLT(idx, 'end_date', e.target.value)}
                                min={lt.start_date || form.week_start}
                                className="h-10 border-[#E2E8DF] text-xs" data-testid={`lt-end-${cat.key}`} />
                            </div>
                          </div>
                          {(parseFloat(lt.cost) || 0) > 0 && lt.start_date && lt.end_date && (() => {
                            const prorated = (() => {
                              const c = parseFloat(lt.cost);
                              const ws2 = new Date(form.week_start); const we2 = new Date(form.week_end);
                              const ls = new Date(lt.start_date); const le = new Date(lt.end_date);
                              const td = Math.max(1, (le - ls) / 86400000 + 1);
                              const os = new Date(Math.max(ws2, ls)); const oe = new Date(Math.min(we2, le));
                              const od = Math.max(0, (oe - os) / 86400000 + 1);
                              return Math.round(c * od / td);
                            })();
                            return <p className="text-[10px] text-[#2D6A4F]">Prorated this week: ₹{prorated.toLocaleString('en-IN')}</p>;
                          })()}
                        </div>
                      );
                    })}
                    {/* Paid Ads Link — shown when any paid ad type has cost */}
                    {longterm.some(lt => ['meta_ads','google_ads','swiggy_ads','zomato_ads','swiggy_cpc','zomato_cpc'].includes(lt.category) && (parseFloat(lt.cost) || 0) > 0) && (
                      <div className="space-y-1.5 pt-2 border-t border-[#E2E8DF]">
                        <Label className="text-xs text-[#5C6B64] flex items-center gap-1"><Link2 className="w-3 h-3" /> Paid Ads Link</Label>
                        <Input type="url" value={val('paid_ads_link')} onChange={e => set('paid_ads_link', e.target.value)}
                          placeholder="https://..." className="h-10 border-[#E2E8DF] text-sm" data-testid="mkt-paid-ads-link" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>

            {/* Adhoc Expenses */}
            <Collapsible open={openSections.adhoc} onOpenChange={() => toggle('adhoc')}>
              <SectionHeader icon={Zap} title="Adhoc Expenses" open={openSections.adhoc} onToggle={() => {}}
                badge={hasAdSpend ? `₹${adhocActualTotal.toLocaleString('en-IN')} actual` : (adhocPlannedTotal > 0 ? `₹${adhocPlannedTotal.toLocaleString('en-IN')} planned` : null)} />
              <CollapsibleContent className="mt-2">
                <Card className="border-[#E2E8DF] shadow-sm">
                  <CardContent className="pt-3 pb-3 space-y-3">
                    {adhocPlannedTotal > 0 && (
                      <p className="text-[10px] text-[#5C6B64] bg-[#F3F5F2] rounded px-2 py-1" data-testid="adhoc-planned-note">
                        Only <strong>Actual</strong> expenses (date ≤ today) count in weekly totals and budget utilization. <strong>Planned</strong> entries are tracked but excluded from reports.
                      </p>
                    )}
                    {/* Influencers */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-[#5C6B64]">Influencers</Label>
                        {(parseFloat(adhoc.influencers.cost) || 0) > 0 && (
                          <Badge className={`text-[9px] ${classifyAdhoc(adhoc.influencers.date) === 'actual' ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#D4A017]/10 text-[#D4A017]'} border-0`}
                            data-testid="ad-influencers-tag">{classifyAdhoc(adhoc.influencers.date) === 'actual' ? 'Actual' : 'Planned'}</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Input type="number" inputMode="decimal" step="any" placeholder="₹0" value={adhoc.influencers.cost}
                          onChange={e => setAd('influencers', 'cost', e.target.value)}
                          className="h-10 border-[#E2E8DF] text-sm" data-testid="ad-influencers-cost" />
                        <Input type="date" value={adhoc.influencers.date} onChange={e => setAd('influencers', 'date', e.target.value)}
                          className="h-10 border-[#E2E8DF] text-xs" data-testid="ad-influencers-date" />
                        <Input type="url" placeholder="Post link" value={adhoc.influencers.post_link}
                          onChange={e => setAd('influencers', 'post_link', e.target.value)}
                          className="h-10 border-[#E2E8DF] text-sm" data-testid="ad-influencers-link" />
                      </div>
                    </div>
                    {/* Paper Ads */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-[#5C6B64]">Paper Ads</Label>
                        {(parseFloat(adhoc.paper_ads.cost) || 0) > 0 && (
                          <Badge className={`text-[9px] ${classifyAdhoc(adhoc.paper_ads.date) === 'actual' ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#D4A017]/10 text-[#D4A017]'} border-0`}
                            data-testid="ad-paper-tag">{classifyAdhoc(adhoc.paper_ads.date) === 'actual' ? 'Actual' : 'Planned'}</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Input type="number" inputMode="decimal" step="any" placeholder="₹0" value={adhoc.paper_ads.cost}
                          onChange={e => setAd('paper_ads', 'cost', e.target.value)}
                          className="h-10 border-[#E2E8DF] text-sm" data-testid="ad-paper-cost" />
                        <Input type="date" value={adhoc.paper_ads.date} onChange={e => setAd('paper_ads', 'date', e.target.value)}
                          className="h-10 border-[#E2E8DF] text-xs" data-testid="ad-paper-date" />
                        <Input type="url" placeholder="Link (optional)" value={adhoc.paper_ads.link}
                          onChange={e => setAd('paper_ads', 'link', e.target.value)}
                          className="h-10 border-[#E2E8DF] text-sm" data-testid="ad-paper-link" />
                      </div>
                    </div>
                    {/* LSM / Sponsorships / Others with date */}
                    {[
                      { key: 'lsm', label: 'LSM' },
                      { key: 'sponsorships', label: 'Sponsorships' },
                      { key: 'others', label: 'Others' },
                    ].map(({ key, label }) => (
                      <div key={key} className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-[#5C6B64]">{label}</Label>
                          {(parseFloat(adhoc[key].cost) || 0) > 0 && (
                            <Badge className={`text-[9px] ${classifyAdhoc(adhoc[key].date) === 'actual' ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#D4A017]/10 text-[#D4A017]'} border-0`}
                              data-testid={`ad-${key}-tag`}>{classifyAdhoc(adhoc[key].date) === 'actual' ? 'Actual' : 'Planned'}</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input type="number" inputMode="decimal" step="any" placeholder="₹0" value={adhoc[key].cost}
                            onChange={e => setAd(key, 'cost', e.target.value)}
                            className="h-10 border-[#E2E8DF] text-sm" data-testid={`ad-${key}-cost`} />
                          <Input type="date" value={adhoc[key].date} onChange={e => setAd(key, 'date', e.target.value)}
                            className="h-10 border-[#E2E8DF] text-xs" data-testid={`ad-${key}-date`} />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>

            {/* Custom Fields */}
            <Collapsible open={openSections.custom} onOpenChange={() => toggle('custom')}>
              <SectionHeader icon={Plus} title="Custom Costs" open={openSections.custom} onToggle={() => {}}
                badge={customTotal > 0 ? `₹${customTotal.toLocaleString('en-IN')}` : null} />
              <CollapsibleContent className="mt-2">
                <Card className="border-[#E2E8DF] shadow-sm">
                  <CardContent className="pt-3 pb-3 space-y-2">
                    {customCosts.map((c, idx) => (
                      <div key={idx} className="flex items-end gap-2">
                        <div className="flex-1">
                          <Input placeholder="Label" value={c.label}
                            onChange={e => { const u = [...customCosts]; u[idx] = { ...u[idx], label: e.target.value }; setCustomCosts(u); }}
                            className="h-10 border-[#E2E8DF] text-sm" data-testid={`mkt-custom-label-${idx}`} />
                        </div>
                        <div className="w-28">
                          <Input type="number" inputMode="decimal" step="any" placeholder="₹0" value={c.amount}
                            onChange={e => { const u = [...customCosts]; u[idx] = { ...u[idx], amount: e.target.value }; setCustomCosts(u); }}
                            className="h-10 border-[#E2E8DF] text-sm" data-testid={`mkt-custom-amount-${idx}`} />
                        </div>
                        {customCosts.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" className="h-10 w-10 p-0 text-[#C45A41] shrink-0"
                            onClick={() => setCustomCosts(p => p.filter((_, i) => i !== idx))}>
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => setCustomCosts(p => [...p, { ...EMPTY_CUSTOM }])}
                      className="w-full h-8 text-xs border-dashed border-[#E2E8DF] text-[#5C6B64] gap-1">
                      <Plus className="w-3 h-3" /> Add
                    </Button>
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>

            </div> {/* end right col */}
            </div> {/* end 2-col grid */}

            {/* Total + Budget Summary + Submit */}
            <Card className="border-[color:var(--color-primary)] shadow-sm bg-[color:var(--color-primary)]/5">
              <CardContent className="py-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-[#1A2421]">Total Marketing Spend</span>
                  <span className="text-lg font-bold text-[color:var(--color-primary)]" data-testid="mkt-total-spend">
                    ₹{Math.round(totalSpend).toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-[#5C6B64]">
                  <span>Long-term: ₹{longtermTotal.toLocaleString('en-IN')}</span>
                  <span>Adhoc (Actual): ₹{adhocActualTotal.toLocaleString('en-IN')}</span>
                  {adhocPlannedTotal > 0 && <span className="text-[#D4A017]">Planned: ₹{adhocPlannedTotal.toLocaleString('en-IN')}</span>}
                  <span>Custom: ₹{customTotal.toLocaleString('en-IN')}</span>
                </div>
                {budgetAmt > 0 && (
                  <div className="mt-2.5 pt-2.5 border-t border-[color:var(--color-primary)]/20 space-y-2" data-testid="budget-summary">
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div>
                        <p className="text-[9px] text-[#5C6B64] uppercase">Budget</p>
                        <p className="text-xs font-bold text-[#1A2421]">₹{Math.round(budgetAmt).toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-[#5C6B64] uppercase">MTD Spent</p>
                        <p className="text-xs font-bold text-[#1A2421]">₹{Math.round(mtdSpend).toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-[#5C6B64] uppercase">Remaining</p>
                        <p className={`text-xs font-bold ${budgetRemaining < 0 ? 'text-[#C45A41]' : 'text-[#2D6A4F]'}`}>
                          {budgetRemaining < 0 ? '-' : ''}₹{Math.abs(Math.round(budgetRemaining)).toLocaleString('en-IN')}
                        </p>
                      </div>
                      <div>
                        <p className="text-[9px] text-[#5C6B64] uppercase">Used</p>
                        <p className={`text-xs font-bold ${budgetColor}`}>{budgetUtilPct}%</p>
                      </div>
                    </div>
                    <div className="h-1.5 bg-[#E2E8DF] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${budgetBarColor}`}
                        style={{ width: `${Math.min(budgetUtilPct, 100)}%` }} />
                    </div>
                    {budgetUtilPct >= 90 && budgetUtilPct < 100 && (
                      <p className="text-[10px] text-[#D4A017] flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Approaching budget limit
                      </p>
                    )}
                    {budgetUtilPct >= 100 && (
                      <p className="text-[10px] text-[#C45A41] flex items-center gap-1 font-medium">
                        <AlertTriangle className="w-3 h-3" /> Budget exceeded
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ITER286-refine — Auto-Calculated Summary: every dashboard KPI derived from the raw inputs above.
                Read-only. Cost/Follower and Cost/Engagement blank when denom is 0. */}
            <Card className="border-[#E2E8DF] shadow-sm bg-white" data-testid="mkt-autocalc-summary">
              <CardContent className="py-3">
                <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-medium mb-3 flex items-center gap-1">
                  <Zap className="w-3 h-3 text-[color:var(--color-primary)]" /> Auto-calculated summary
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-center">
                  <SummaryCell label="Followers Added"
                    value={openF > 0 && closeF > 0
                            ? `${growth >= 0 ? '+' : ''}${growth.toLocaleString('en-IN')}`
                            : '—'}
                    color={growth < 0 ? 'text-[#C45A41]' : 'text-[#1A2421]'}
                    testid="ac-followers-added" />
                  <SummaryCell label="Growth %"
                    value={growthPct === null ? '—' : `${growthPct >= 0 ? '+' : ''}${growthPct.toFixed(1)}%`}
                    color={growthPct !== null && growthPct < 0 ? 'text-[#C45A41]' : 'text-[#1A2421]'}
                    testid="ac-growth-pct" />
                  <SummaryCell label="Avg Views / Post"
                    value={avgViews === null ? '—' : avgViews.toLocaleString('en-IN')}
                    testid="ac-avg-views" />
                  <SummaryCell label="Avg Reach / Post"
                    value={avgReach === null ? '—' : avgReach.toLocaleString('en-IN')}
                    testid="ac-avg-reach" />
                  <SummaryCell label="Avg Impressions / Post"
                    value={avgImpr === null ? '—' : avgImpr.toLocaleString('en-IN')}
                    testid="ac-avg-impr" />
                  <SummaryCell label="Total Engagement"
                    value={engagementN > 0 ? engagementN.toLocaleString('en-IN') : '—'}
                    testid="ac-total-engagement" />
                  <SummaryCell label="Engagement Rate"
                    value={engagementRatePct === null ? '—' : `${engagementRatePct.toFixed(2)}%`}
                    testid="ac-er" />
                  <SummaryCell label="Cost / Follower"
                    value={growth > 0 && totalSpend > 0
                            ? `₹${(totalSpend / growth).toFixed(2)}` : '—'}
                    testid="ac-cost-per-follower" />
                  <SummaryCell label="Cost / Engagement"
                    value={engagementN > 0 && totalSpend > 0
                            ? `₹${(totalSpend / engagementN).toFixed(2)}` : '—'}
                    testid="ac-cost-per-engagement" />
                  <SummaryCell label="Budget Utilisation"
                    value={budgetAmt > 0 ? `${budgetUtilPct}%` : '—'}
                    color={budgetColor}
                    testid="ac-budget-util" />
                  <SummaryCell label="MER (headline)" value="—"
                    hint="Auto-computed on the dashboard from finalized P&L. Requires the current month's P&L to be closed."
                    testid="ac-mer" />
                  <SummaryCell label="Incremental ROI" value="—"
                    hint="Auto-computed on the dashboard. Requires 3 finalized months of P&L for the baseline."
                    testid="ac-incremental-roi" />
                </div>
                <p className="text-[10px] italic text-[#5C6B64] mt-3">
                  These values are derived automatically from the raw fields above. MER &amp; Incremental ROI are computed on the dashboard against finalized P&amp;L data — you don&apos;t need to enter them here.
                </p>
              </CardContent>
            </Card>

            {moduleWritable ? (
              <Button type="submit" disabled={submitting}
                className="w-full h-11 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white font-medium active:scale-[0.98] transition-all"
                data-testid="mkt-submit-btn">
                {submitting ? 'Saving...' : 'Save Marketing Entry'}
              </Button>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-[#F3F5F2] rounded-lg" data-testid="mkt-view-only">
                <Lock className="w-3.5 h-3.5 text-[#5C6B64]" />
                <span className="text-xs text-[#5C6B64] font-medium">View Only Access</span>
                <span className="text-[10px] text-[#5C6B64] ml-auto">Module not assigned to you</span>
              </div>
            )}
          </form>
        </TabsContent>

        {/* ─── HISTORY ─── */}
        <TabsContent value="list">
          {loadingEntries ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : entries.length === 0 ? (
            <Card className="border-[#E2E8DF] shadow-sm">
              <CardContent className="py-10 text-center">
                <p className="text-sm text-[#5C6B64]">No marketing entries yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {entries.map((e, i) => (
                <Card key={i} className="border-[#E2E8DF] shadow-sm cursor-pointer hover:border-[color:var(--color-primary)]/30 transition-colors"
                  onClick={() => loadEntry(e)} data-testid={`mkt-entry-${i}`}>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-[#1A2421]">{e.week_start} → {e.week_end}</span>
                      <Badge variant="outline" className="text-[10px] border-[#E2E8DF] text-[#5C6B64]">
                        ₹{Math.round(e.total_spend || 0).toLocaleString('en-IN')}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div>
                        <p className="text-[10px] text-[#5C6B64]">Growth</p>
                        <p className={`text-xs font-semibold ${(e.follower_growth || 0) >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                          {(e.follower_growth || 0) >= 0 ? '+' : ''}{e.follower_growth || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#5C6B64]">Posts</p>
                        <p className="text-xs font-semibold text-[#1A2421]">{e.num_posts}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#5C6B64]">Views</p>
                        <p className="text-xs font-semibold text-[#1A2421]">{(e.total_views || 0).toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#5C6B64]">LT | Ad</p>
                        <p className="text-xs font-semibold text-[#5C6B64]">
                          ₹{Math.round(e.longterm_total || 0).toLocaleString('en-IN')} | ₹{Math.round(e.adhoc_total || 0).toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
